import './styles/impairment.css';

import {format} from 'date-fns';
import {Search} from 'lucide-react';
import {useEffect, useMemo, useState} from 'react';

import {apiService} from '../../../apiService';
import {useAuth} from '../../../contexts/AuthContext';
import {
  generateDateRange,
  sortEmployeesByName,
  useFilterDates,
} from '../../../utils';
import {DateRangeFilter} from '../../common';
import {HeatmapKey, HeatmapTable} from './components';

export const Impairment = () => {
  const {currentUser} = useAuth();

  const [isMultiOrg, setIsMultiOrg] = useState(false);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');

  const {date, setDate} = useFilterDates(31);

  const dates = useMemo(
    () => generateDateRange(date.startDate, date.endDate),
    [date],
  );

  useEffect(() => {
    if (!currentUser?.uid) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const uid = currentUser.uid;

        const adminInfo = await apiService.get(
          `/api/check-user-admin-status?uid=${uid}`,
        );

        const orgCount = adminInfo.org_count ?? 1;
        setIsMultiOrg(adminInfo.role === 'admin' || orgCount > 1);

        const start_date = format(date.startDate, 'yyyy-MM-dd');
        const end_date = format(date.endDate, 'yyyy-MM-dd');

        let url = '/api/impairment-metrics?';
        url += `start_date=${start_date}`;
        url += `&end_date=${end_date}`;
        url += `&uid=${uid}`;

        const response = await apiService.get(url);
        setData(response.body ?? []);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [currentUser?.uid, date]);

  return (
    <div className='impairment-container'>
      <h1 className='impairment-title'>Impairment Heatmap</h1>

      <div className='impairment-toolbar'>
        <DateRangeFilter onApply={setDate} />
        <HeatmapKey />
      </div>

      {isLoading ? (
        // TODO: Replace with a proper loader
        <div className='impairment-loading'>Loading...</div>
      ) : (
        <div className='impairment-org'>
          {!isMultiOrg ? (
            <div className='impairment-search-container'>
              <Search className='impairment-search-icon' />
              <input
                type='text'
                placeholder='Search employee....'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className='impairment-search-input'
              />
            </div>
          ) : null}
          {data.map((org) => {
            const sortedEmployees = sortEmployeesByName(org.employees);

            if (search) {
              const lowerSearch = search.toLowerCase();
              sortedEmployees.splice(
                0,
                sortedEmployees.length,
                ...sortedEmployees.filter(
                  (emp) =>
                    emp.first_name.toLowerCase().includes(lowerSearch) ||
                    emp.last_name.toLowerCase().includes(lowerSearch) ||
                    `${emp.first_name} ${emp.last_name}`
                      .toLowerCase()
                      .includes(lowerSearch),
                ),
              );
            }

            if (sortedEmployees.length === 0) {
              return null;
            }

            return (
              <div key={org.org_id} className='impairment-org-block'>
                {isMultiOrg && (
                  <h2 className='impairment-org-title'>{org.org_name}</h2>
                )}

                <HeatmapTable data={sortedEmployees} dates={dates} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
